/**
 * HandlungsempfehlungenCardContent Component
 * Displays Handlungsempfehlungen (Action Recommendations) data in a card layout
 * Shows top 3 recommendations as individual cards side by side
 */

import { useTranslation } from 'react-i18next';
import { Clock, Users } from 'lucide-react';
import { getPriorityColor, getEffectColor, flattenHandlungsempfehlungenTabelle } from '@/types/handlungsempfehlungen';
import type { HandlungsempfehlungenFullData } from '@/types/handlungsempfehlungen';

export interface HandlungsempfehlungenCardContentProps {
  data: HandlungsempfehlungenFullData;
}

/**
 * Card content for Handlungsempfehlungen displaying top 3 recommendations
 */
export function HandlungsempfehlungenCardContent({
  data,
}: HandlungsempfehlungenCardContentProps) {
  const { t } = useTranslation();
  const { main } = data;

  // Get top 3 recommendations sorted by priority
  const topRecommendations = [...flattenHandlungsempfehlungenTabelle(main.Handlungsempfehlungen_tabelle)]
    .sort((a, b) => a.Priorität - b.Priorität)
    .slice(0, 3);

  return (
    <div className="h-full flex flex-col">
      {/* 3 Cards side by side - full height */}
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
        {topRecommendations.map((recommendation, index) => (
          <div
            key={index}
            className="bg-[var(--glass-bg)] rounded-xl border border-[var(--glass-border)] p-4 flex flex-col hover:shadow-md transition-shadow h-full"
          >
            {/* Number badge on top */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold text-white mb-3 shadow-sm"
              style={{
                background: index === 0
                  ? 'linear-gradient(135deg, #FC03B0, #FF6803)'
                  : index === 1
                    ? 'linear-gradient(135deg, #E8148A, #FC03B0)'
                    : 'linear-gradient(135deg, #f97316, #fb923c)',
              }}
            >
              {index + 1}
            </div>

            {/* Name */}
            <h4 className="text-sm font-semibold text-[var(--color-charcoal)] line-clamp-2 mb-2 leading-snug">
              {recommendation.Name}
            </h4>

            {/* Target group - more prominent */}
            <div className="flex items-start gap-2 mb-3 flex-1">
              <Users className="h-4 w-4 text-[var(--color-primary-start)] flex-shrink-0 mt-0.5" />
              <span className="text-xs text-[var(--color-charcoal)]/80 leading-relaxed line-clamp-3">
                {recommendation.Zielgruppe}
              </span>
            </div>

            {/* Priority & Effect row - compact at bottom */}
            <div className="flex items-center justify-between pt-3 border-t border-[var(--glass-border)]">
              {/* Priority */}
              <div className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getPriorityColor(recommendation.Priorität) }}
                />
                <span className="text-[10px] text-[var(--color-charcoal)]/60">
                  P{recommendation.Priorität}
                </span>
              </div>

              {/* Effect */}
              <div className="flex items-center gap-1">
                <Clock
                  className="h-3 w-3 flex-shrink-0"
                  style={{ color: getEffectColor(recommendation.Effekt_nach_Monaten) }}
                />
                <span
                  className="text-[10px] font-medium"
                  style={{ color: getEffectColor(recommendation.Effekt_nach_Monaten) }}
                >
                  {recommendation.Effekt_nach_Monaten} {t('results.cards.handlungsempfehlungen.card.months')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

export default HandlungsempfehlungenCardContent;
